// src/pages/Events.jsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import INFO from "../data/user";
import SEO from "../data/seo";
import { fetchPublicContent } from "../data/publicContent";

import "./styles/events.css";

// Swap in your real Vercel deployment URL once it's live.
const CHECKOUT_ENDPOINT = "https://jabuzwane.vercel.app/api/create-event-checkout";

const TIER_COLORS = ["#0d9488", "#08776f", "#5d7170", "#849794", "#172d2b", "#1b746c"];

function parseTicketPrice(value) {
	const normalized = typeof value === "string"
		? value.trim().replace(/^R\s*/i, "").replace(/,/g, "")
		: value;
	if (normalized === "") return null;
	const price = Number(normalized);
	return Number.isFinite(price) && price >= 0 ? price : null;
}

function formatCurrency(val, currency = "ZAR") {
	if (parseTicketPrice(val) === 0) return "Free";
	return new Intl.NumberFormat("en-ZA", {
		style: "currency",
		currency,
		maximumFractionDigits: 0,
	}).format(val);
}

function formatDateParts(iso) {
	if (!iso) return null;
	const d = new Date(`${iso}T00:00:00`);
	return {
		day: d.getDate(),
		month: d.toLocaleDateString("en-ZA", { month: "short" }).toUpperCase(),
		weekday: d.toLocaleDateString("en-ZA", { weekday: "long" }),
		full: d.toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }),
	};
}

function todayISO() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function lowestPrice(event) {
	const tierPrices = (event.ticketTiers || [])
		.map((tier) => parseTicketPrice(tier.price))
		.filter((price) => price !== null);
	const candidates = [...tierPrices];
	const basePrice = parseTicketPrice(event.basePrice);
	if (basePrice !== null) candidates.push(basePrice);
	if (!candidates.length) return 0;
	return Math.min(...candidates);
}

// Remaining stock for a tier — null means unlimited (no quantity cap set).
function remainingFor(tier) {
	const limit = tier.quantity || 0;
	if (limit <= 0) return null;
	const sold = tier.sold || 0;
	const reserved = tier.reserved || 0;
	return Math.max(0, limit - sold - reserved);
}

// Whether an event still has at least one tier a customer could buy.
// Events with no tiers at all (flat basePrice only) are treated as always available.
function eventHasAvailableStock(event) {
	const tiers = event.ticketTiers || [];
	if (tiers.length === 0) return true;
	return tiers.some((t) => {
		const remaining = remainingFor(t);
		return remaining === null || remaining > 0;
	});
}

// ── Scroll-reveal hook ──────────────────────────────────────────────────────

function useReveal() {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const node = ref.current;
		if (!node) return;
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					io.disconnect();
				}
			},
			{ threshold: 0.15 }
		);
		io.observe(node);
		return () => io.disconnect();
	}, []);

	return [ref, visible];
}

// ── Embedded styles — matches the neutral catalog look used across the
// Books/BookSuccess pages: white cards, light gray page background, Inter
// font, near-black buttons, thin gray borders. Kept inline so this page's
// look doesn't depend on whatever's already in events.css.
const EVENTS_CSS = `
.ev-page {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  background: radial-gradient(circle at 50% 0%, #e3f7f4 0, #f4f9f8 38%, #fff 78%);
  padding: 124px 20px 96px;
  color: #1a1d1f;
  border-radius: 0;
}

.ev-container {
  max-width: 1000px;
  margin: 0 auto;
}

/* ── header ── */

.ev-header {
  text-align: center;
  max-width: 560px;
  margin: 0 auto 36px;
}

.ev-header__eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--link-color);
  margin-bottom: 12px;
}

.ev-header__title {
  font-weight: 750;
  font-size: clamp(36px, 5vw, 52px);
  line-height: 1.06;
  margin: 0 0 12px;
  letter-spacing: -0.045em;
}

.ev-header__sub {
  font-size: 15px;
  line-height: 1.6;
  color: #6b7280;
  margin: 0;
}

/* ── states ── */

.ev-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #6b7280;
  font-size: 14px;
  text-align: center;
}

.ev-state--error { color: #c0392b; }
.ev-state__icon { font-size: 36px; }

.ev-state__spinner {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 3px solid #e9eaec;
  border-top-color: #1a1d1f;
  animation: ev-spin 0.8s linear infinite;
}

@keyframes ev-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ev-state__spinner { animation: none; }
}

/* ── shared: perforated ticket edge + date badge ── */

.ev-perf {
  position: relative;
  height: 1px;
  background: repeating-linear-gradient(to right, #d5d8dc 0 6px, transparent 6px 12px);
  margin: 0 20px;
}

.ev-perf__notch {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #f6f7f8;
}

.ev-perf__notch--top { top: -8px; left: -8px; }
.ev-perf__notch--bottom { top: -8px; right: -8px; }

.ev-date-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e9eaec;
  box-shadow: 0 4px 10px rgba(20, 22, 26, 0.08);
  flex-shrink: 0;
}

.ev-date-badge--lg {
  width: 64px;
  height: 64px;
  border-radius: 14px;
}

.ev-date-badge__day {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a1d1f;
}

.ev-date-badge--lg .ev-date-badge__day { font-size: 22px; }

.ev-date-badge__month {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.ev-stub-price {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ev-stub-price__label {
  font-size: 11.5px;
  color: #9aa1ab;
}

.ev-stub-price__value {
  font-size: 16px;
  font-weight: 700;
  color: #1a1d1f;
}

/* ── buttons ── */

.ev-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 999px;
  padding: 11px 18px;
  cursor: pointer;
  border: 1.5px solid transparent;
  text-decoration: none;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
}

.ev-btn--primary {
  background: var(--link-color);
  color: #fff;
  box-shadow: 0 8px 18px rgba(13, 148, 136, 0.22);
}
.ev-btn--primary:hover:not(.ev-btn--disabled) {
  background: var(--link-color-strong);
  transform: translateY(-1px);
}

.ev-btn--ghost {
  background: #fff;
  color: var(--link-color);
  border-color: rgba(13, 148, 136, .22);
}
.ev-btn--ghost:hover:not(.ev-btn--disabled) {
  border-color: #9aa1ab;
  background: #f6f7f8;
}

.ev-btn--static { cursor: default; }

.ev-btn--disabled {
  background: transparent;
  color: #9aa1ab;
  border: 1.5px dashed #e9eaec;
  cursor: default;
}

.ev-btn--yoco {
  background: var(--link-color);
  color: #fff;
}
.ev-btn--yoco:hover:not(:disabled) {
  background: var(--link-color-strong);
  transform: translateY(-1px);
}
.ev-btn--yoco:disabled {
  opacity: 0.6;
  cursor: default;
}

.ev-yoco-mark {
  font-weight: 800;
  letter-spacing: 0.03em;
}

/* ── hero ticket ── */

.ev-hero-ticket {
  background: #ffffff;
  border: 1px solid rgba(15, 31, 61, .09);
  border-radius: 26px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(15, 31, 61, 0.1);
  margin-bottom: 40px;
}

.ev-hero-ticket__inner { padding: 28px 30px 24px; }

.ev-hero-ticket__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--link-color);
  margin-bottom: 14px;
}

.ev-hero-ticket__main {
  display: grid;
  grid-template-columns: minmax(230px, 300px) minmax(0, 1fr);
  gap: clamp(22px, 4vw, 44px);
  align-items: center;
}

.ev-hero-ticket__media {
  width: 100%;
  border-radius: 18px;
  overflow: hidden;
  aspect-ratio: 4 / 5;
}

.ev-hero-ticket__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ev-hero-ticket__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.ev-hero-ticket__title {
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 750;
  margin: 4px 0 0;
  line-height: 1.25;
}

.ev-hero-ticket__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}

.ev-hero-ticket__desc {
  font-size: 14.5px;
  line-height: 1.65;
  color: #4b5563;
  margin: 4px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ev-hero-ticket__stub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 30px 22px;
  background: #f8fbff;
}

.ev-hero-ticket__tiers {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}
.ev-hero-ticket__tiers--open {
  max-height: 900px;
}
.ev-hero-ticket__tiers--open .ev-tiers {
  padding: 0 24px 22px;
}

/* ── event grid + card ── */

.ev-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 22px;
}

.ev-ticket {
  background: #ffffff;
  border: 1px solid #e9eaec;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(20, 22, 26, 0.04), 0 6px 16px rgba(20, 22, 26, 0.05);
  opacity: 0;
  transform: translateY(12px);
  transition: opacity 0.4s ease, transform 0.4s ease, box-shadow 0.2s ease;
}

.ev-ticket--in {
  opacity: 1;
  transform: translateY(0);
}

.ev-ticket:hover {
  box-shadow: 0 2px 6px rgba(20, 22, 26, 0.05), 0 14px 28px rgba(20, 22, 26, 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .ev-ticket { opacity: 1; transform: none; transition: none; }
}

.ev-ticket__media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.ev-ticket__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ev-ticket__media-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  background: #f0ecf5;
}

.ev-ticket__media .ev-date-badge {
  position: absolute;
  top: 12px;
  left: 12px;
}

.ev-ticket__body {
  padding: 16px 18px 4px;
}

.ev-ticket__title {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.35;
}

.ev-ticket__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 8px;
}

.ev-ticket__desc {
  font-size: 13px;
  line-height: 1.55;
  color: #1a1d1f;
  margin: 0 0 4px;
}

.ev-ticket .ev-perf { margin: 14px 18px 0; }

.ev-ticket__stub {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px 16px;
}

.ev-ticket__tiers {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}
.ev-ticket__tiers--open {
  max-height: 900px;
}
.ev-ticket__tiers--open .ev-tiers {
  padding: 0 18px 16px;
}

/* ── tier list + buy panel ── */

.ev-tiers {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ev-tier-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  background: #f6f7f8;
  border: 1px solid #e9eaec;
  border-radius: 10px;
}

.ev-tier-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ev-tier-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1d1f;
}

.ev-tier-remaining {
  font-size: 11.5px;
  color: #9aa1ab;
}

.ev-tier-remaining--out {
  color: #c0392b;
}

.ev-tier-price {
  margin-left: auto;
  font-size: 13.5px;
  font-weight: 700;
  color: #1a1d1f;
}

.ev-buy-panel {
  width: 100%;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed #e9eaec;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ev-buy-panel__intro {
	display: flex;
	flex-direction: column;
	gap: 3px;
	padding: 12px 14px;
	border-radius: 12px;
	background: linear-gradient(135deg, #f0f8f7, #f8fbfb);
	border: 1px solid #d7ece9;
}

.ev-buy-panel__intro strong { font-size: 13px; color: #1a1d1f; }
.ev-buy-panel__intro span { font-size: 11.5px; color: #6b7280; }

.ev-buy-panel__row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.ev-buy-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ev-buy-field__label {
  font-size: 11.5px;
  font-weight: 600;
  color: #6b7280;
}

.ev-qty-stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e9eaec;
  border-radius: 8px;
  padding: 4px 8px;
  background: #fff;
  width: fit-content;
}

.ev-qty-stepper button {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid #e9eaec;
  background: #f6f7f8;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #1a1d1f;
}

.ev-qty-stepper button:disabled {
  opacity: 0.4;
  cursor: default;
}

.ev-qty-stepper span {
  min-width: 16px;
  text-align: center;
  font-size: 13.5px;
  font-weight: 600;
}

.ev-buy-panel__email {
  font-size: 13.5px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e9eaec;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.ev-buy-panel__email:focus {
  outline: 2px solid var(--link-color);
  outline-offset: 1px;
}

.ev-buy-panel__input {
	min-width: 0;
	font-size: 13px;
	padding: 9px 10px;
	border-radius: 9px;
	border: 1px solid #e0e2e6;
	font-family: inherit;
	width: 100%;
	box-sizing: border-box;
	background: #fff;
	transition: border-color .15s ease, box-shadow .15s ease;
}

.ev-buy-panel__input:focus {
	outline: none;
	border-color: var(--link-color);
	box-shadow: 0 0 0 3px rgba(13, 148, 136, .12);
}

.ev-buy-panel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.ev-buy-total {
  font-size: 13px;
  color: #6b7280;
}

.ev-buy-total strong {
  color: #1a1d1f;
  font-size: 14px;
}

.ev-buy-error {
  font-size: 12.5px;
  color: #c0392b;
  background: #fbeae6;
  padding: 8px 10px;
  border-radius: 8px;
}

/* ── responsive ── */

@media (max-width: 640px) {
  .ev-page { padding: 104px 16px 72px; }
  .ev-header { text-align: left; margin-bottom: 28px; }
  .ev-hero-ticket__main { grid-template-columns: 1fr; }
  .ev-hero-ticket__media { width: 100%; }
	.ev-hero-ticket__inner { padding: 20px; }
	.ev-hero-ticket__stub { padding: 16px 20px; }
	.ev-buy-panel__row { flex-direction: column; }
	.ev-buy-field { width: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .ev-hero-ticket__tiers,
  .ev-ticket__tiers {
    transition: none;
  }
}
`;

// ======================================================
// Ticket tier breakdown (expands inside a card) — now with
// remaining-stock display and a Yoco checkout buy flow.
// ======================================================

const TierList = ({ event, tiers }) => {
	const sorted = [...tiers].sort((a, b) => b.price - a.price);

	const [buyingId, setBuyingId] = useState(null);
	const [qty, setQty] = useState({});
	const [buyerName, setBuyerName] = useState("");
	const [buyerSurname, setBuyerSurname] = useState("");
	const [buyerPhone, setBuyerPhone] = useState("");
	const [buyerEmail, setBuyerEmail] = useState("");
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState(null);
	const isFreeTier = (tier) => parseTicketPrice(tier.price) === 0;

	const setQtyFor = (tierId, next, max) => {
		const clamped = Math.max(1, Math.min(max || 10, next));
		setQty((q) => ({ ...q, [tierId]: clamped }));
	};

	const handleBuy = async (tier) => {
		if (!buyerName.trim() || !buyerSurname.trim() || !buyerPhone.trim() || !buyerEmail.trim()) {
			setErr("Complete your name, surname, cellphone number, and email first.");
			return;
		}
		setBusy(true);
		setErr(null);
		try {
			const res = await fetch(CHECKOUT_ENDPOINT, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					eventId: event.id,
					tierId: tier.id,
					quantity: qty[tier.id] || 1,
					buyerName: buyerName.trim(),
					buyerSurname: buyerSurname.trim(),
					buyerPhone: buyerPhone.trim(),
					buyerEmail: buyerEmail.trim(),
				}),
			});
			const data = await res.json();
			if (!res.ok) throw new Error(data.message || "Could not start checkout.");
			window.location.href = data.redirectUrl;
		} catch (e) {
			setErr(e.message);
			setBusy(false);
		}
	};

	return (
		<div className="ev-tiers">
			{sorted.map((t, i) => {
				const remaining = remainingFor(t);
				const soldOut = remaining !== null && remaining <= 0;
				const currentQty = qty[t.id] || 1;
				const total = t.price * currentQty;

				return (
					<div className="ev-tier-row" key={t.id || t.name}>
						<span className="ev-tier-dot" style={{ background: TIER_COLORS[i % TIER_COLORS.length] }} />
						<span className="ev-tier-name">{t.name}</span>
						{remaining !== null && (
							<span className={`ev-tier-remaining${soldOut ? " ev-tier-remaining--out" : ""}`}>
								{soldOut ? "Sold out" : `${remaining} left`}
							</span>
						)}
						<span className="ev-tier-price">{formatCurrency(t.price)}</span>

						{!soldOut && (
							<button
								className="ev-btn ev-btn--ghost"
								onClick={() => {
									setErr(null);
									setBuyingId((id) => (id === t.id ? null : t.id));
								}}
							>
								{buyingId === t.id ? "Cancel" : "Buy"}
							</button>
						)}

						{buyingId === t.id && (
							<div className="ev-buy-panel">
								<div className="ev-buy-panel__intro">
									<strong>Ticket holder details</strong>
									<span>These details will appear on your ticket.</span>
								</div>
								<div className="ev-buy-panel__row">
									<div className="ev-buy-field">
										<span className="ev-buy-field__label">Quantity</span>
										<div className="ev-qty-stepper">
											<button
												type="button"
												onClick={() => setQtyFor(t.id, currentQty - 1, remaining)}
												disabled={currentQty <= 1}
												aria-label="Decrease quantity"
											>
												–
											</button>
											<span>{currentQty}</span>
											<button
												type="button"
												onClick={() => setQtyFor(t.id, currentQty + 1, remaining)}
												disabled={remaining !== null && currentQty >= remaining}
												aria-label="Increase quantity"
											>
												+
											</button>
										</div>
									</div>

									<div className="ev-buy-field">
										<span className="ev-buy-field__label">Name</span>
										<input className="ev-buy-panel__input" type="text" placeholder="Name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
									</div>
									<div className="ev-buy-field">
										<span className="ev-buy-field__label">Surname</span>
										<input className="ev-buy-panel__input" type="text" placeholder="Surname" value={buyerSurname} onChange={(e) => setBuyerSurname(e.target.value)} />
									</div>
									<div className="ev-buy-field">
										<span className="ev-buy-field__label">Cellphone number</span>
										<input className="ev-buy-panel__input" type="tel" placeholder="082 123 4567" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} />
									</div>
									<div className="ev-buy-field" style={{ flex: 1 }}>
										<span className="ev-buy-field__label">Email address</span>
										<input
											className="ev-buy-panel__input"
											type="email"
											placeholder="you@example.com"
											value={buyerEmail}
											onChange={(e) => setBuyerEmail(e.target.value)}
										/>
									</div>
								</div>

								<div className="ev-buy-panel__footer">
									<span className="ev-buy-total">
										Total <strong>{formatCurrency(total)}</strong>
									</span>

									<button
										className="ev-btn ev-btn--yoco"
										disabled={busy}
										onClick={() => handleBuy(t)}
									>
										{busy ? (
											"Redirecting…"
										) : (
											isFreeTier(t) ? "Get free ticket" : <>Pay with <span className="ev-yoco-mark">YOCO</span></>
										)}
									</button>
								</div>

								{err && <div className="ev-buy-error">{err}</div>}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

// ======================================================
// Hero ticket — the next upcoming event, oversized
// ======================================================

const HeroTicket = ({ event }) => {
	const [open, setOpen] = useState(false);
	const dateParts = formatDateParts(event.date);
	const hasTiers = (event.ticketTiers || []).length > 0;
	const hasStock = eventHasAvailableStock(event);
	const from = lowestPrice(event);

	return (
		<div className="ev-hero-ticket">
			<div className="ev-hero-ticket__inner">
				<div className="ev-hero-ticket__eyebrow">Next up</div>

				<div className="ev-hero-ticket__main">
					{event.coverUrl ? (
						<div className="ev-hero-ticket__media">
							<img src={event.coverUrl} alt={event.title} />
						</div>
					) : null}

					<div className="ev-hero-ticket__content">
						{dateParts && (
							<div className="ev-date-badge ev-date-badge--lg">
								<span className="ev-date-badge__day">{dateParts.day}</span>
								<span className="ev-date-badge__month">{dateParts.month}</span>
							</div>
						)}

						<h2 className="ev-hero-ticket__title">{event.title}</h2>

						<div className="ev-hero-ticket__meta">
							{dateParts && <span>{dateParts.weekday}, {dateParts.full}</span>}
							{event.time && <span>· {event.time}</span>}
							{event.location && <span>· {event.location}</span>}
						</div>

						{event.description && <p className="ev-hero-ticket__desc">{event.description}</p>}
					</div>
				</div>
			</div>

			<div className="ev-perf" aria-hidden="true">
				<span className="ev-perf__notch ev-perf__notch--top" />
				<span className="ev-perf__notch ev-perf__notch--bottom" />
			</div>

			<div className="ev-hero-ticket__stub">
				<div className="ev-stub-price">
					<span className="ev-stub-price__label">{hasTiers ? "From" : "Ticket"}</span>
					<span className="ev-stub-price__value">
						{!hasStock ? "Sold out" : from === 0 ? "Free" : formatCurrency(from)}
					</span>
				</div>

				{!hasStock ? (
					<span className="ev-btn ev-btn--primary ev-btn--static ev-btn--disabled">Sold out</span>
				) : hasTiers ? (
					<button className="ev-btn ev-btn--primary" onClick={() => setOpen((o) => !o)}>
						{open ? "Hide tiers" : "Get tickets"}
					</button>
				) : (
					<span className="ev-btn ev-btn--primary ev-btn--static">Get tickets</span>
				)}
			</div>

			<div className={`ev-hero-ticket__tiers${open ? " ev-hero-ticket__tiers--open" : ""}`}>
				{hasTiers && <TierList event={event} tiers={event.ticketTiers} />}
			</div>
		</div>
	);
};

// ======================================================
// Event ticket card (grid)
// ======================================================

const EventTicketCard = ({ event, index }) => {
	const [ref, visible] = useReveal();
	const [open, setOpen] = useState(false);
	const dateParts = formatDateParts(event.date);
	const hasTiers = (event.ticketTiers || []).length > 0;
	const hasStock = eventHasAvailableStock(event);
	const from = lowestPrice(event);

	return (
		<div
			ref={ref}
			className={`ev-ticket${visible ? " ev-ticket--in" : ""}`}
			style={{ transitionDelay: `${Math.min(index, 6) * 70}ms` }}
		>
			<div className="ev-ticket__media">
				{event.coverUrl ? (
					<img src={event.coverUrl} alt={event.title} loading="lazy" />
				) : (
					<div className="ev-ticket__media-placeholder">🎫</div>
				)}
				{dateParts && (
					<div className="ev-date-badge">
						<span className="ev-date-badge__day">{dateParts.day}</span>
						<span className="ev-date-badge__month">{dateParts.month}</span>
					</div>
				)}
			</div>

			<div className="ev-ticket__body">
				<h3 className="ev-ticket__title">{event.title}</h3>
				<div className="ev-ticket__meta">
					{event.time && <span>{event.time}</span>}
					{event.location && <span>{event.location}</span>}
				</div>
				{event.description && <p className="ev-ticket__desc">{event.description}</p>}
			</div>

			<div className="ev-perf" aria-hidden="true">
				<span className="ev-perf__notch ev-perf__notch--top" />
				<span className="ev-perf__notch ev-perf__notch--bottom" />
			</div>

			<div className="ev-ticket__stub">
				<div className="ev-stub-price">
					<span className="ev-stub-price__label">{hasTiers ? "From" : "Ticket"}</span>
					<span className="ev-stub-price__value">
						{!hasStock ? "Sold out" : from === 0 ? "Free" : formatCurrency(from)}
					</span>
				</div>
				{!hasStock ? (
					<span className="ev-btn ev-btn--ghost ev-btn--static ev-btn--disabled">Sold out</span>
				) : hasTiers ? (
					<button className="ev-btn ev-btn--ghost" onClick={() => setOpen((o) => !o)}>
						{open ? "Hide" : "Tickets"}
					</button>
				) : (
					<span className="ev-btn ev-btn--ghost ev-btn--static">Tickets</span>
				)}
			</div>

			<div className={`ev-ticket__tiers${open ? " ev-ticket__tiers--open" : ""}`}>
				{hasTiers && <TierList event={event} tiers={event.ticketTiers} />}
			</div>
		</div>
	);
};

// ======================================================
// Events Page
// ======================================================

const Events = () => {
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Forces a re-render periodically so an event that crosses midnight
	// (or sells out via another tab) drops out of "upcoming" without
	// needing a full page refresh.
	const [, forceTick] = useState(0);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		const id = setInterval(() => forceTick((n) => n + 1), 5 * 60 * 1000); // every 5 min
		return () => clearInterval(id);
	}, []);

	useEffect(() => {
		let cancelled = false;
		fetchPublicContent("events")
			.then((data) => {
				if (!cancelled) {
					data.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
					setEvents(data);
				}
			})
			.catch((err) => {
				if (!cancelled) {
					console.error("Could not load events:", err);
					setError(err.message);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => { cancelled = true; };
	}, []);

	const today = todayISO();
	// Past events never reappear here — no more falling back to showing
	// expired events when there's nothing upcoming.
	const upcoming = events.filter((e) => !e.date || e.date >= today);
	const [featured, ...rest] = upcoming;

	const currentSEO = SEO.find((item) => item.page === "events") || {};

	return (
		<>
			<Helmet>
				<title>{`Events | ${INFO.main.title}`}</title>
				<meta
					name="description"
					content={currentSEO.description || "Upcoming events and workshops."}
				/>
				<meta
					name="keywords"
					content={currentSEO.keywords ? currentSEO.keywords.join(", ") : "events"}
				/>
			</Helmet>

			<style>{EVENTS_CSS}</style>

			<div className="page-content">
				<NavBar active="events" />

				<div className="content-wrapper">
					<div className="about-logo-container">
						<div className="about-logo">
							<Logo width={46} />
						</div>
					</div>

					<section className="ev-page">
						<div className="ev-container">
							<header className="ev-header">
								<div className="ev-header__eyebrow">Upcoming</div>
								<h1 className="ev-header__title">Events</h1>
								<p className="ev-header__sub">
									Workshops, seminars, speaking engagements and community
									events designed to inspire growth.
								</p>
							</header>

							{loading && (
								<div className="ev-state">
									<div className="ev-state__spinner" aria-hidden="true" />
									<span>Loading events…</span>
								</div>
							)}

							{error && (
								<div className="ev-state ev-state--error">
									Could not load events. Please try again later.
								</div>
							)}

							{!loading && !error && upcoming.length === 0 && (
								<div className="ev-state">
									<div className="ev-state__icon">🎉</div>
									<p>No upcoming events at the moment.</p>
								</div>
							)}

							{!loading && !error && featured && (
								<HeroTicket event={featured} />
							)}

							{rest.length > 0 && (
								<div className="ev-grid">
									{rest.map((event, i) => (
										<EventTicketCard key={event.id} event={event} index={i} />
									))}
								</div>
							)}
						</div>
					</section>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</>
	);
};

export default Events;
